import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';

import Moment from 'moment';
import { Box, CircularProgress, Drawer } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { formatPolicy } from '../../policies.services';
import { getPolicyDetails } from '../../../../policies/PolicyService';
import {
  ProductTypes,
  isExcessProduct,
  type ProductType,
} from '../../../../types';
import { determineIsLegacyProduct } from '../../../agencies/quotes/PrimeX/utils/primeX.util';
import {
  getFullPrimeXPolicyDetails,
  getPolicyDetailsExcess,
} from '../../../../api/policies.api';
import { getData } from '../../../../utils/functional/fp';
import { getConsolidatedP100CoverageKeys } from '../../../workflows/quotes/prime-100/utils/coverages';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import { getRegionByProduct } from '../../../../i18n/utils';

/**
 * Custom hook for position-based dragging via top/left instead of transform.
 * MUI Drawer's Slide applies its own CSS transform, so react-draggable
 * (which also uses transform) cannot work here.
 */
function useDragPosition() {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const start = useRef({ x: 0, y: 0 });

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only primary button
      if (e.button !== 0) return;
      dragging.current = true;
      start.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
      e.preventDefault();
    },
    [offset]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      setOffset({
        x: e.clientX - start.current.x,
        y: e.clientY - start.current.y,
      });
    };
    const onMouseUp = () => {
      dragging.current = false;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const reset = useCallback(() => {
    dragging.current = false;
    setOffset({ x: 0, y: 0 });
  }, []);

  return { offset, onMouseDown, reset };
}

export default function PolicyDrawer({
  open,
  onClose,
  policyId,
  product,
  policyNumber,
  fetchLatestPolicyByStatus,
  onFetch,
  children,
}: any) {
  const classes = useStyles();
  const notifications = useNotifications();
  const { offset, onMouseDown, reset } = useDragPosition();

  // Reset position when drawer closes
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handlePolicyDetailsErrorCatch = (axiosError: any) => {
    if (_.get(axiosError, 'response.status', 404) !== 404) {
      notifications.error('Policy Details missing at this time');
    } else {
      throw new Error(axiosError);
    }
  };

  const {
    data: policy,
    isInitialLoading,
    ...queryRequest
  } = useQuery(
    ['policy', policyId],
    () =>
      typeof onFetch === 'function'
        ? onFetch(
            policyId,
            product,
            policyNumber,
            fetchLatestPolicyByStatus
          ).catch(handlePolicyDetailsErrorCatch)
        : fetchPolicyDetails({
            policyId,
            product,
          }).catch(handlePolicyDetailsErrorCatch),
    {
      refetchOnWindowFocus: false,
      enabled: open,
    }
  );

  return (
    <Drawer
      // @ts-expect-error TS(2322): Type '{ children: any[]; pt: number; anchor: "righ... Remove this comment to see the full error message
      pt={2}
      anchor="right"
      open={open}
      onClose={onClose}
      classes={classes}
      PaperProps={{
        style: {
          top: `calc(6rem + ${offset.y}px)`,
          right: `calc(17px - ${offset.x}px)`,
        },
      }}
    >
      {/* Drag handle */}
      <Box
        onMouseDown={onMouseDown}
        sx={{
          cursor: 'grab',
          height: '0.75rem',
          flexShrink: 0,
          '&:active': { cursor: 'grabbing' },
        }}
      />
      {isInitialLoading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="13rem"
          width="42rem"
        >
          <CircularProgress size="5rem" />
        </Box>
      )}
      {!isInitialLoading && typeof children === 'function'
        ? children({ policy, ...queryRequest })
        : null}
    </Drawer>
  );
}

const useStyles = makeStyles(({ palette }) => ({
  paper: {
    height: 'auto',
    minWidth: '47rem',
    maxHeight: 'calc(100% - 12rem)',
    padding: '1rem',
    position: 'fixed',
    top: '6rem',
    right: 17,
    zIndex: 10,
    overflow: 'visible',
    color: palette.primary.main,
    backgroundColor: palette.background.paper,
    borderRadius: '0.9rem',
  },
}));

export const fetchPolicyDetails = ({ policyId, product }: any) => {
  if (determineIsLegacyProduct(product)) {
    // @ts-expect-error TS(2554): Expected 3 arguments, but got 2.
    return getPolicyDetails(policyId, product).then(({ data: rawPolicy }) => {
      const policy = {
        ..._.pick(rawPolicy, [
          'currency',
          'expiresOn',
          'id',
          'accountId',
          'additionalInsureds',
          'coveragesAdded',
          'coveragesRemoved',
          'coveragesUpdated',
          'boundOn',
          'quoteId',
          'quoteNumber',
          'policyNumber',
          'premium',
          'agentFirstName',
          'agentLastName',
          'agentEmail',
          'agentPhone',
          'isPaid',
          'isPrimePlus',
          'isCertificateAvailable',
          'isDocumentAvailable',
          'status',
          'noKnownLossLetterReq',
          'created',
          'modified',
          'totalPremium',
          'companyName',
          'description',
          'product',
          'status',
          'isSurplus',
          'customerFirstName',
          'customerLastName',
          'customerEmail',
        ]),
        ..._.pick(rawPolicy.firmographicData, [
          'address1',
          'city',
          'state',
          'zipCode',
        ]),
        ..._.pick(rawPolicy.policyResponse, ['totalPremium']),
        ...(product === ProductTypes.p250 && {
          ..._.pick(rawPolicy.initialRequestData, [
            'deductible',
            'limit',
            'effectiveDate',
            'isRenewal',
            'isEndorsement',
            'coverages',
            'address',
            'endDate',
            'additionalInsureds',
          ]),
        }),
        ...(product === ProductTypes.p100 && {
          ..._.pick(rawPolicy, [
            'deductible',
            'limit',
            'effectiveDate',
            'isRenewal',
            'isEndorsement',
            'endDate',
          ]),
          waitingPeriod: rawPolicy?.waitingPeriod ?? 0,
          retroactivePeriod: rawPolicy?.retroactivePeriod,
          // Coverages
          coverages: {
            ..._.pick(rawPolicy, getConsolidatedP100CoverageKeys()),
          },
        }),
      };

      return {
        ...formatPolicy(policy),
        effectiveDate: policy.effectiveDate
          ? Moment.utc(policy.effectiveDate).format('MMM DD, YYYY hh:mm A')
          : null,
      };
    });
  }

  if (isExcessProduct(product)) {
    return fetchExcessPolicyDetails(policyId);
  }

  return getFullPrimeXPolicyDetails(product, policyId)
    .then(getData)
    .then(getData)
    .then(formatPolicy);
};

function unwrapExcessPayload(payload: { data?: unknown } | null | undefined) {
  return payload?.data ?? payload;
}

function asNumber(value: unknown) {
  if (value == null || value === '') return undefined;
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return undefined;
  return parsed;
}

function getIsoCurrencyForProduct(product: string) {
  const language = getRegionByProduct(product as ProductType);
  if (language === 'en-GB') return 'GBP';
  if (language === 'en-AU') return 'AUD';
  return 'USD';
}

function flattenExcessPolicyForSummary(rawPolicy: Record<string, any>) {
  const policy = { ...(rawPolicy.policy ?? {}), ...rawPolicy };
  const initialRequest = policy.initialRequestData ?? {};
  const policyResponse = policy.policyResponse ?? {};

  return {
    ...policy,
    limit: asNumber(policy.limit ?? initialRequest.limit ?? policy.aggLimit),
    // formatPolicy derives Policy Expiration and Policy Term from
    // effectiveDate/expiresOn. Excess payloads carry these as endDate (and the
    // adhoc form as coverage*Date), so normalize them or both fields stay blank.
    effectiveDate:
      policy.effectiveDate ??
      initialRequest.effectiveDate ??
      initialRequest.coverageStartDate,
    expiresOn:
      policy.expiresOn ??
      policy.endDate ??
      initialRequest.endDate ??
      initialRequest.coverageEndDate,
    // Excess uses attachment, not deductible. Do not copy either into this
    // slot or the summary labels $1M attachment as Deductible.
    deductible: undefined,
    totalPremium: asNumber(
      policy.totalPremium ?? policyResponse.totalPremium ?? policy.premium
    ),
    currency: policy.currency || getIsoCurrencyForProduct(policy.product),
  };
}

function fetchExcessPolicyDetails(policyId: string) {
  return getPolicyDetailsExcess(policyId)
    .then(getData)
    .then(unwrapExcessPayload)
    .then(flattenExcessPolicyForSummary)
    .then(formatPolicy);
}
